import { PageSEO, Practice, Service } from './types';
import { COMPANY_NAME } from './site';

// ---------------------------------------------------------------------------
// Canonical service taxonomy. This is the ONE source of truth for services --
// the services overview, both practice hubs, every service detail page, the
// header dropdown, the footer, and the contact form all read from PRACTICES
// and the helpers below.
//
// Two practices, eleven services. What a service has to earn its place here:
//   - a certification the team holds (`certification`), or
//   - copy that already existed in the repo describing the work.
// Nothing is listed because it is a plausible thing a ServiceNow shop might
// sell. Categories the company cannot evidence were deleted outright rather
// than redistributed into a neighbouring service -- an earlier revision did
// the opposite, which left one bucket carrying 16 capabilities while others
// carried one or two. See REMEDIATION_NOTES.md ("Services rebuild") for the
// full list of capability tokens deleted and why.
//
// `navLabel` is what appears in menus and on cards. `title` is the page <h1>
// and the SEO title -- longer, in title case, and specific enough to stand
// alone in a search result. They are deliberately different strings.
//
// COPY REGISTER: professional B2B services copy. Second person, no sentence
// over 25 words, and no metric, client, or credential that does not already
// exist in this repository -- which is none, so no page quotes a number.
// `engagements` are written and titled as the services offered within each
// platform area, because that is how a buyer reads them. The banned marketing
// words and the sentence-length limit are both enforced in services.test.ts.
// ---------------------------------------------------------------------------

const serviceNowServices: Service[] = [
  {
    navLabel: 'ITSM',
    slug: 'itsm',
    title: 'ServiceNow ITSM Implementation',
    practiceSlug: 'servicenow',
    iconKey: 'itsm',
    certification: 'CIS-ITSM',
    summary:
      'A single system of record for incidents, changes, and requests across your IT organization.',
    // Change management and CAB routing lead deliberately: it is the deepest
    // delivery evidence in the repo, so it is the example the page opens with.
    problem:
      'Change approvals happen over email and in conversation, leaving no reliable audit trail. CAB meetings review lists that are already out of date, while incidents and requests are tracked somewhere else entirely. Visibility suffers, and accountability follows it.',
    engagements: [
      {
        name: 'ITSM Implementation',
        description:
          'We configure incident, problem, change, and request management around the way your organization already operates. The result is a platform your teams adopt rather than work around.',
      },
      {
        name: 'Process Redesign on an Existing Instance',
        description:
          'We assess the processes configured in your instance today and rebuild the workflows that create friction. Configuration nobody uses is retired rather than carried forward.',
      },
      {
        name: 'Change & CAB Optimization',
        description:
          'We route change requests through the approval chain your organization already follows. Standard changes progress without waiting on a weekly meeting, and every emergency change leaves a complete record.',
      },
      {
        name: 'Upgrade Readiness',
        description:
          'We review your customizations ahead of an upgrade and identify what will conflict. You enter the upgrade window with a plan rather than a list of surprises.',
      },
    ],
    capabilities: [
      'Change Management',
      'CAB approval routing',
      'Incident Management',
      'Major Incident Management',
      'Problem Management',
      'Release Management',
      'Request Fulfillment',
      'Configuration Management',
      'Service Portfolio Management',
    ],
    faqs: [
      {
        question: 'Do we need to replace our current ticketing system first?',
        answer:
          'No. We scope the ServiceNow build around your existing environment and plan the transition with you. Running both systems in parallel through cutover is a normal approach.',
      },
      {
        question: 'Can you work within the instance we already have?',
        answer:
          'Yes. Process redesign on an existing instance is one of the services above. We begin by reviewing what is configured today and how much of it is genuinely used.',
      },
      {
        question: 'Who needs to be involved from our side?',
        answer:
          'The process owner and a platform administrator, at minimum. Change and CAB work also involves your current approvers, since their approval chain is what we build around.',
      },
    ],
    proofSlot: null,
  },
  {
    navLabel: 'CMDB',
    slug: 'cmdb',
    title: 'ServiceNow CMDB & Data Foundations',
    practiceSlug: 'servicenow',
    iconKey: 'cmdb',
    certification: 'CIS-DF',
    summary:
      'Configuration data your teams trust, and the service relationships that make it useful.',
    problem:
      'Most CMDBs are populated once and drift from that point forward. Teams fall back on their own spreadsheets because they no longer trust the platform, which accelerates the drift. Impact analysis becomes guesswork at the moment it matters most.',
    engagements: [
      {
        name: 'CMDB Health Assessment',
        description:
          'We evaluate completeness, duplication, and staleness against the way your data is actually used. You receive a prioritized remediation plan rather than a raw audit.',
      },
      {
        name: 'CSDM Alignment',
        description:
          'We map your records to the Common Service Data Model that the platform is designed around. Service-level reporting depends on getting that alignment right.',
      },
      {
        name: 'Discovery Deployment & Tuning',
        description:
          'We deploy Discovery and tune the schedules and probes to your environment. Well-tuned Discovery collects what matters without filling the CMDB with noise.',
      },
      {
        name: 'Data Remediation & Governance',
        description:
          'We clean up existing records and establish ownership for each class going forward. Governance is what keeps the data accurate once the project ends.',
      },
    ],
    capabilities: [
      'CMDB',
      'Discovery',
      'Service Mapping',
      'Event Management',
      'CSDM alignment',
      'Asset Management',
      'Hardware/Software Asset Management',
      'Reporting',
    ],
    faqs: [
      {
        question: 'Do we need Discovery to maintain a CMDB?',
        answer:
          'Not necessarily, but you do need a dependable way to keep records current. Discovery is the usual answer, and an integration with an authoritative source can cover part of the estate.',
      },
      {
        question: 'Why is asset management included here?',
        answer:
          'Hardware and software asset records depend on the same configuration data. Separating them into another engagement means maintaining the same information twice.',
      },
      {
        question: 'Where does reporting fit in?',
        answer:
          'Reporting belongs with the data it draws on. Once the underlying records are accurate, the reports built on them become dependable.',
      },
    ],
    proofSlot: null,
  },
  {
    // Certification-backed, but no delivered reference for it exists in this
    // repo. The copy below is deliberately capability framing rather than
    // experience claims, and deliberately shorter than ITSM and CMDB. Do not
    // pad it to match their length -- see REMEDIATION_NOTES.md.
    navLabel: 'CSM',
    slug: 'csm',
    title: 'ServiceNow Customer Service Management',
    practiceSlug: 'servicenow',
    iconKey: 'csm',
    certification: 'CIS-CSM',
    summary:
      'One channel for customers to raise issues and follow them through to resolution.',
    problem:
      'When customer requests arrive by email, visibility ends at the inbox. Work is not routed by rule, ownership stays informal, and customers cannot check progress without asking someone to look it up.',
    engagements: [
      {
        name: 'CSM Implementation',
        description:
          'We configure ServiceNow Customer Service Management so every request becomes a tracked record with a clear owner and status. Everything else here builds on that foundation.',
      },
      {
        name: 'Customer Portal & Self-Service',
        description:
          'Your customers get a portal to submit cases and follow their progress. Self-service resolves the requests that only ever needed an answer.',
      },
      {
        name: 'Case Management Design',
        description:
          'We design case types, routing rules, and lifecycle states before configuration begins. This design work decides whether the finished system feels better than the email it replaced.',
      },
      {
        name: 'CRM Integration',
        description:
          'Account and contact records stay in your CRM and flow through to cases. Your agents work from current customer data without re-entering it.',
      },
    ],
    capabilities: ['Customer Service Management', 'Customer and Partner Portals'],
    faqs: [
      {
        question: 'How is this different from ITSM?',
        answer:
          'ITSM manages work raised by your internal teams, and CSM manages work raised by your customers. Both can share a single platform and a single CMDB.',
      },
      {
        question: 'What do you need from us to begin?',
        answer:
          'The case types your team handles and the owner of each one. If a CRM holds your customer records, we need to know which system stays authoritative.',
      },
    ],
    proofSlot: null,
  },
  {
    navLabel: 'Integrations & Automation',
    slug: 'integrations-automation',
    title: 'ServiceNow Integrations & Automation',
    practiceSlug: 'servicenow',
    iconKey: 'automation',
    certification: 'CAD',
    summary:
      'Connected systems and automated handoffs, so your teams stop moving data by hand.',
    problem:
      'When a record is updated in one system and never reaches the next, two versions of the truth appear. Manual re-entry fills the gap, and the resulting errors surface later in reporting nobody can reconcile.',
    engagements: [
      {
        name: 'Integration Design & Build',
        description:
          'We define the exchange first: what moves, in which direction, and how failures are handled. The build follows, using IntegrationHub or a scripted REST API as the case requires.',
      },
      {
        name: 'Flow Designer & IntegrationHub Development',
        description:
          'Approvals, notifications, and record updates run as maintainable flows rather than business rules scattered across the instance. Your administrators can see exactly what ran and why.',
      },
      {
        name: 'Integration Diagnosis & Remediation',
        description:
          'When an inherited integration stops working, we trace the failure and restore service. We also add the error handling and alerting it was missing.',
      },
      {
        name: 'Automation Opportunity Assessment',
        description:
          'We review where your teams repeat the same manual steps and assess each one for automation. You receive a prioritized list, including what we recommend leaving alone.',
      },
    ],
    capabilities: [
      'IntegrationHub',
      'Scripted REST APIs',
      'Flow Designer',
      'Third-party system integrations',
    ],
    faqs: [
      {
        question: 'Can you integrate a system that has no API?',
        answer:
          'Often, yes. A file-based exchange or a database view can serve as the interface, though it changes what the integration can guarantee. We set that expectation before building.',
      },
      {
        question: 'How are overnight failures handled?',
        answer:
          'That is a design decision made up front. We agree whether a failure retries, queues for review, or raises an incident someone will actually see.',
      },
    ],
    proofSlot: null,
  },
  {
    navLabel: 'Custom Applications',
    slug: 'custom-applications',
    title: 'Custom Applications on the Now Platform',
    practiceSlug: 'servicenow',
    iconKey: 'customDev',
    certification: 'CAD',
    summary: 'Purpose-built applications on the platform your organization already licenses.',
    problem:
      'Some processes have no matching ServiceNow product and end up in spreadsheets maintained by one person. That work stays invisible to reporting, difficult to audit, and fragile whenever its owner is unavailable.',
    engagements: [
      {
        name: 'Scoped Application Development on App Engine',
        description:
          'We build applications in a dedicated scope so they upgrade cleanly and cannot affect the rest of your platform. Your administrators can maintain them.',
      },
      {
        name: 'Service Portal & UI Builder',
        description:
          'We build interfaces designed around the task in front of the user. People complete their work without platform training or navigating form and list views.',
      },
      {
        name: 'Legacy Customization Modernization',
        description:
          'Customizations written into the global scope years ago make every upgrade harder than the last. We move that logic into patterns the platform supports.',
      },
    ],
    capabilities: [
      'App Engine',
      'Scoped applications',
      'Service Portal',
      'Employee Center',
      'UI Builder',
    ],
    faqs: [
      {
        question: 'Will a custom application survive a platform upgrade?',
        answer:
          'That is what scoping is for. An application built in its own scope is insulated from the global changes an upgrade introduces.',
      },
      {
        question: 'Can our team maintain it after handover?',
        answer:
          'Yes, and that is the point of building it here. It uses the administration skills your team already has, and documentation is handed over with it.',
      },
      {
        question: 'When would you recommend against a custom application?',
        answer:
          'When a ServiceNow product already covers the requirement. We will tell you that rather than build a second version of something you own.',
      },
    ],
    proofSlot: null,
  },
  {
    navLabel: 'Testing & Platform Health',
    slug: 'testing-platform-health',
    title: 'ServiceNow Testing & Platform Health',
    practiceSlug: 'servicenow',
    iconKey: 'testing',
    certification: 'CSA',
    summary: 'Automated test coverage for the workflows your business depends on.',
    problem:
      'Upgrades and configuration changes can break a workflow without any immediate signal. The first indication is often a user report several days later, by which point the cause is difficult to trace.',
    engagements: [
      {
        name: 'ATF Suite Development',
        description:
          'We build automated tests for the workflows you cannot afford to lose. Coverage runs on demand instead of depending on a manual script and available time.',
      },
      {
        name: 'Upgrade Regression Testing',
        description:
          'Tests run against sub-production before an upgrade reaches your users. Issues are found and resolved inside the upgrade window rather than after it.',
      },
      {
        name: 'Instance Health Assessment',
        description:
          'We review configuration, customization, and performance against platform standards. You receive a prioritized view of what is most likely to cause problems at your next upgrade.',
      },
    ],
    capabilities: [
      'Automated Test Framework (ATF)',
      'Upgrade regression testing',
      'Instance health assessment',
    ],
    faqs: [
      {
        question: 'We test manually today. What does automation add?',
        answer:
          'Manual testing is the first thing dropped when an upgrade window tightens. Automated coverage runs consistently regardless of schedule pressure.',
      },
      {
        question: 'Which workflows should we automate first?',
        answer:
          'The ones with the highest cost of silent failure: approvals, integrations, and any process your customers touch directly.',
      },
    ],
    proofSlot: null,
  },
  {
    navLabel: 'Training & Enablement',
    slug: 'training-enablement',
    title: 'ServiceNow Training & Enablement',
    practiceSlug: 'servicenow',
    iconKey: 'training',
    certification: null,
    summary: 'Practical enablement delivered by the engineers who build on the platform.',
    // The site's existing "ServiceNow certified instructors and curriculum
    // developers" claim is deliberately NOT restated here. Nothing in this
    // repo verifies it -- see CLAIMS REQUIRING VERIFICATION in
    // REMEDIATION_NOTES.md.
    problem:
      'A platform rollout without structured enablement gets adopted at a fraction of its potential. Teams revert to the processes they already know, and the platform takes the blame for the shortfall.',
    engagements: [
      {
        name: 'Administrator & Developer Enablement',
        description:
          'Your administrators and developers work through the platform using your own configuration. The examples come from your instance, not a generic demo environment.',
      },
      {
        name: 'Custom Curriculum',
        description:
          'We develop material scoped to your implementation: videos, quick reference cards, and guided tours. The material stays useful long after the session ends.',
      },
      {
        name: 'Post-Implementation Knowledge Transfer',
        description:
          'Your team is walked through what was built and the reasoning behind each decision. Knowledge transfer is scheduled work inside the engagement.',
      },
    ],
    capabilities: ['Videos', 'Quick Reference Cards', 'Guided Tours'],
    faqs: [
      {
        question: 'Do you train administrators or end users?',
        answer:
          'Both, in separate sessions. Administrators need the reasoning behind the configuration, and end users need to complete their work with confidence.',
      },
      {
        question: 'What do we keep afterwards?',
        answer:
          'All of the material. Videos, reference cards, and guided tours remain in your instance and belong to your organization.',
      },
    ],
    proofSlot: null,
  },
];

// ---------------------------------------------------------------------------
// Cloud & AI. No AWS certification exists anywhere in this repo, so nothing
// in this practice implies one.
// ---------------------------------------------------------------------------

const cloudAiServices: Service[] = [
  {
    navLabel: 'Cloud Architecture',
    slug: 'cloud-architecture',
    title: 'AWS Cloud Architecture',
    practiceSlug: 'cloud-ai',
    iconKey: 'cloud',
    certification: null,
    summary: 'An AWS account structure and architecture your team can operate with confidence.',
    problem:
      'Cloud environments tend to grow faster than the design behind them. Accounts are created as they are needed, permissions accumulate, and ownership of running workloads becomes unclear.',
    engagements: [
      {
        name: 'Landing Zone & Multi-Account Setup',
        description:
          'We establish account separation, guardrails, and centralized logging using Organizations and Control Tower. New accounts follow an established pattern rather than a support ticket.',
      },
      {
        name: 'Well-Architected Review',
        description:
          'We assess an existing workload against the AWS Well-Architected pillars. Findings are ranked by risk, with a clear recommendation on what to address first.',
      },
      {
        name: 'Migration Planning',
        description:
          'We plan what moves, in what sequence, and what each workload depends on. The plan identifies the work your own team is equipped to carry out.',
      },
    ],
    capabilities: [
      'AWS Organizations',
      'AWS Control Tower',
      'Amazon Aurora',
      'Amazon DynamoDB',
      'AWS Amplify',
    ],
    faqs: [
      {
        question: 'We are already running on AWS. Is this still relevant?',
        answer:
          'Yes. Most of this work happens on established environments: separating accounts, tightening permissions, and reviewing workloads that outgrew their original design.',
      },
      {
        question: 'How do you approach a migration?',
        answer:
          'We start with dependencies and sequence. Understanding what each workload relies on determines the order of the move, and the order determines the risk.',
      },
    ],
    proofSlot: null,
  },
  {
    navLabel: 'Serverless Applications',
    slug: 'serverless-applications',
    title: 'Serverless Application Development',
    practiceSlug: 'cloud-ai',
    iconKey: 'serverless',
    certification: null,
    summary: 'Applications built on managed services, with no servers to size or maintain.',
    problem:
      'Smaller applications rarely justify a dedicated server, yet that server still needs patching, monitoring, and capacity planning. The maintenance burden usually outlives whoever set it up.',
    engagements: [
      {
        name: 'Application Development',
        description:
          'We build applications as functions behind managed APIs, using Lambda with AppSync or a REST endpoint. There is no instance to patch or scale by hand.',
      },
      {
        name: 'Legacy Application Modernization',
        description:
          'Applications running on ageing servers are rebuilt against managed services. Step Functions carries the processes with genuine workflow behind them.',
      },
    ],
    capabilities: [
      'AWS Lambda',
      'AWS Step Functions',
      'AWS AppSync',
      'Amazon DynamoDB',
      'AWS Amplify',
    ],
    faqs: [
      {
        question: 'Is serverless less expensive?',
        answer:
          'It depends on your traffic profile. Sustained heavy load can be more economical on provisioned capacity, while variable or intermittent load usually favors serverless.',
      },
      {
        question: 'What happens if we outgrow it?',
        answer:
          'Managed services scale well beyond what most applications require. Where a single component stops fitting, that component is replaced without rebuilding the rest.',
      },
    ],
    proofSlot: null,
  },
  {
    navLabel: 'AI-Assisted Workflows',
    slug: 'ai-assisted-workflows',
    title: 'AI-Assisted Workflow Automation',
    practiceSlug: 'cloud-ai',
    iconKey: 'ai',
    certification: null,
    summary: "AI applied to the specific tasks that consume your team's time.",
    problem:
      'General-purpose AI features are rarely adopted, because they are not built around how anyone actually works. Meanwhile the repetitive work continues: reviewing documents, triaging requests, and drafting the same responses.',
    engagements: [
      {
        name: 'Workflow Opportunity Assessment',
        description:
          'We identify where your teams repeat the same work and assess which of those steps a model can handle reliably. You receive a shortlist and the reasoning behind our exclusions.',
      },
      {
        name: 'Document Processing',
        description:
          'Invoices, forms, and reports are read and converted into structured records. We build on Bedrock and route low-confidence results to a person for review.',
      },
      {
        name: 'Human-in-the-Loop Approval Design',
        description:
          'People stay in the process wherever an error would be costly. We design those checkpoints before any step is automated.',
      },
    ],
    capabilities: [
      'Amazon Bedrock',
      'AWS Lambda',
      'AWS Step Functions',
      'Document processing',
      'Human-in-the-loop review',
    ],
    faqs: [
      {
        question: 'Will this replace roles on our team?',
        answer:
          'No. These builds remove repetitive steps and keep your people on the decisions that carry judgment and risk.',
      },
      {
        question: 'What happens when the model gets it wrong?',
        answer:
          'That case is designed for rather than hoped against. A low-confidence result routes to a person instead of being written through automatically.',
      },
    ],
    proofSlot: null,
  },
  {
    // This is managing a client's AWS estate: monitoring, patching, cost
    // control, support retainers. Website hosting is ONE capability line
    // within it, not the framing -- see REMEDIATION_NOTES.md.
    navLabel: 'Managed Hosting & Support',
    slug: 'managed-hosting-support',
    title: 'Managed AWS Hosting & Support',
    practiceSlug: 'cloud-ai',
    iconKey: 'hosting',
    certification: null,
    summary: 'Ongoing monitoring, maintenance, and support for your AWS environment.',
    problem:
      'An environment handed over without a support model degrades quietly. Patching slips, cost drifts upward with nobody reviewing it, and failures are often reported by customers before anyone internally notices.',
    engagements: [
      {
        name: 'Managed AWS Operations',
        description:
          'We monitor and patch your accounts and keep them inside the guardrails they were built with. Cost review is part of the service rather than an annual exercise.',
      },
      {
        name: 'Application Hosting',
        description:
          'Applications we build, and applications you already operate, are hosted and kept running.',
      },
      {
        name: 'Support Retainer',
        description:
          'An agreed allocation of time for changes, escalations, and the work that falls outside a project. You always know who to contact.',
      },
    ],
    capabilities: [
      'Monitoring and alerting',
      'Patching and maintenance',
      'Cost review',
      'AWS Amplify hosting',
      'Website hosting',
      'Support retainers',
    ],
    faqs: [
      {
        question: 'Do you support environments you did not build?',
        answer:
          'Yes, following a review. We need a clear picture of what is running and how it was configured before taking responsibility for keeping it running.',
      },
      {
        question: 'What does a retainer cover?',
        answer:
          'Scope is agreed per engagement: the systems covered, the time included, and what counts as an escalation. All of it is documented before work begins.',
      },
    ],
    proofSlot: null,
  },
];

export const PRACTICES: Practice[] = [
  {
    slug: 'servicenow',
    name: 'ServiceNow',
    navDescription: 'Implementation, integration, and ongoing platform support.',
    positioning:
      'ServiceNow is our core practice. We implement the platform, connect it to the systems around it, and support it well beyond go-live.',
    services: serviceNowServices,
    proofSlot: null,
  },
  {
    slug: 'cloud-ai',
    name: 'Cloud & AI',
    navDescription: 'AWS architecture, serverless development, and AI automation.',
    positioning:
      'This practice serves organizations already running on AWS, or planning a move to it. It covers the cloud infrastructure, applications, and AI-assisted automation that keep your business running day to day.',
    // The "Technologies We Work With" band this hub carried is removed. The
    // technology names still appear in the individual services' capability
    // lists, and six of the nine are still not referenced anywhere else in
    // this repository -- see CLAIMS REQUIRING VERIFICATION in
    // REMEDIATION_NOTES.md.
    services: cloudAiServices,
    proofSlot: null,
  },
];

export const ALL_SERVICES: Service[] = PRACTICES.flatMap((practice) => practice.services);

export function getPracticeBySlug(slug: string): Practice | undefined {
  return PRACTICES.find((practice) => practice.slug === slug);
}

export function getPracticeForService(service: Service): Practice | undefined {
  return PRACTICES.find((practice) => practice.slug === service.practiceSlug);
}

/**
 * The one service lookup. Both segments are required, because a service is
 * only ever addressable under its own practice -- the flat
 * `getServiceBySlug(slug)` helper this replaces let the same content resolve
 * from a second URL shape, which is the duplication this structure exists to
 * remove.
 */
export function findService(
  practiceSlug: string | undefined,
  serviceSlug: string | undefined
): Service | undefined {
  const practice = practiceSlug ? getPracticeBySlug(practiceSlug) : undefined;
  if (!practice) return undefined;
  return practice.services.find((service) => service.slug === serviceSlug);
}

/** Canonical path for a practice hub. */
export function practicePath(practice: Practice): string {
  return `/services/${practice.slug}`;
}

/** Canonical path for a service detail page: /services/<practice>/<service>. */
export function servicePath(service: Service): string {
  return `/services/${service.practiceSlug}/${service.slug}`;
}

/**
 * Practice and service metadata are derived here rather than duplicated into
 * PAGE_SEO, so adding a service to the taxonomy above is the only edit
 * needed -- the route, the title, and the canonical all follow from it.
 */
export function getServiceSeo(service: Service): PageSEO {
  return {
    path: servicePath(service),
    title: `${service.title} | ${COMPANY_NAME}`,
    description: service.summary,
  };
}

export function getPracticeSeo(practice: Practice): PageSEO {
  return {
    path: practicePath(practice),
    title: `${practice.name} Services | ${COMPANY_NAME}`,
    description: practice.positioning,
  };
}

/**
 * Whether a practice's hub shows the certification band. True when any of its
 * services is certification-backed; the band itself lists every certification
 * the team holds, not just the ones this practice references.
 *
 * Gated at the call site by SHOW_CERTIFICATIONS -- certification display is
 * currently switched off site-wide. See content/certifications.ts.
 */
export function practiceHasCertifications(practice: Practice): boolean {
  return practice.services.some((service) => service.certification !== null);
}

/**
 * Services that lead their practice's hub. These three carry both a
 * certification and delivered work behind them, so they go first on the card
 * grid; the rest follow in taxonomy order.
 *
 * This is display order only. The taxonomy order in PRACTICES is unchanged,
 * because it is what the contact form, the footer, and the nav read.
 */
const LEAD_SERVICE_SLUGS = ['itsm', 'cmdb', 'integrations-automation'];

export function hubServiceOrder(practice: Practice): Service[] {
  const rank = (service: Service) => {
    const index = LEAD_SERVICE_SLUGS.indexOf(service.slug);
    return index === -1 ? LEAD_SERVICE_SLUGS.length : index;
  };
  return [...practice.services].sort((a, b) => rank(a) - rank(b));
}

export const SERVICES_PAGE_COPY = {
  hero: {
    title: 'Services',
    lead: 'Technology that improves how work gets done',
  },
  certificationsLabel: 'Certifications our team holds',
  // The engagement-process heading moved to content/home.ts, next to the steps
  // it labels -- the section is shared with the homepage now.
};
